/*
 * Finds contact details in a text and masks them.
 *
 * The rules are data, not code. The page gets a rule set from the server
 * and passes it in. The Python engine in
 * tools/text-filter/text_filter/__init__.py reads the same rule set and
 * must give the same result, so keep the two files in step.
 */

export const ALL = 'all';

// What counts as part of a word for the keyword boundaries. \b is no use
// here, because for \b an umlaut is not a word character.
const WORD_CHARS = '0-9A-Za-z_À-ÿ';

const REGEX_SPECIAL = '\\^$.|?*+()[]{}';

// Returns null for a pattern that does not compile, so one bad rule in the
// database can not take the whole filter down.
function compile(pattern, where, flags = 'gi') {
    try {
        return new RegExp(pattern, flags);
    } catch (ex) {
        console.error(`text-filter: skipping ${where}, pattern does not compile: ${ex.message}`);
        return null;
    }
}

function escape_keyword(keyword) {
    let chars = [];

    for (const c of keyword) {
        const lower = c.toLowerCase();
        const upper = c.toUpperCase();

        if (REGEX_SPECIAL.includes(c)) {
            chars.push('\\' + c);

        // The Python engine only matches a-z case-insensitive, so both
        // engines spell out both cases of an umlaut.
        } else if (c.codePointAt(0) > 127 && lower != upper
            && [...lower].length == 1 && [...upper].length == 1) {
            chars.push(`[${lower}${upper}]`);

        } else {
            chars.push(c);
        }
    }

    return chars.join('');
}

/*
 * Builds one pattern out of a keyword list. boundary is one of
 *
 * none:   match anywhere, also inside a longer word
 * start:  match at the start of a word
 * word:   match whole words only
 */
export function keyword_pattern(keywords, boundary = 'none') {
    let words = [...new Set((keywords ?? [])
        .map(k => (k ?? '').trim())
        .filter(k => k.length > 0))];

    if (words.length == 0) {
        return null;
    }

    // Longest first, so KK03_gmbh wins over KK03.
    words.sort((a, b) => ([...b].length - [...a].length) || (a < b ? -1 : a > b ? 1 : 0));

    const body = words.map(escape_keyword).join('|');

    const before = ['start', 'word'].includes(boundary) ? `(?<![${WORD_CHARS}])` : '';
    const after = boundary == 'word' ? `(?![${WORD_CHARS}])` : '';

    return `${before}(?:${body})${after}`;
}

/* A compiled rule set. Build it once and keep it, compiling is the slow part. */
export class RuleSet {

    constructor(data) {
        const self = this;

        self.data = data;
        self.whitelist = [];
        self.rules = new Map();

        for (const entry of data.whitelist ?? []) {
            if (entry.active === false) {
                continue;
            }

            const regex = compile(entry.pattern, `whitelist entry ${entry.name}`);

            if (regex) {
                self.whitelist.push(regex);
            }
        }

        for (const rule of data.rules ?? []) {
            if (rule.active === false) {
                continue;
            }

            const pattern = rule.type == 'keywords'
                ? keyword_pattern(rule.keywords, rule.boundary ?? 'none')
                : rule.pattern;

            // A keyword rule without keywords has nothing to look for.
            if (!pattern) {
                continue;
            }

            const regex = compile(pattern, `rule ${rule.name}`);

            if (!regex) {
                continue;
            }

            // An exception has to match at the start of the hit.
            const exceptions = (rule.exceptions ?? [])
                .map(p => compile(`^(?:${p})`, `exception of rule ${rule.name}`, 'i'))
                .filter(x => x);

            self.rules.set(rule.name, {
                name: rule.name,
                rule_set: rule.set,
                regex: regex,
                exceptions: exceptions
            });
        }
    }

    /*
     * rules can be ALL, a rule name, a set name, or a list of those.
     * Names we do not know are skipped, because a rule can be switched off
     * in the database at any time.
     */
    select(rules = ALL) {
        const self = this;

        if (rules === null || rules === undefined || rules === ALL) {
            return [...self.rules.values()];
        }

        if (typeof rules == 'string') {
            rules = [rules];
        }

        let selected = new Map();

        for (const name of rules) {
            if (name == ALL) {
                self.rules.forEach((rule, rule_name) => selected.set(rule_name, rule));

            } else if (self.rules.has(name)) {
                selected.set(name, self.rules.get(name));

            } else {
                self.rules.forEach((rule, rule_name) => {
                    if (rule.rule_set == name) {
                        selected.set(rule_name, rule);
                    }
                });
            }
        }

        return [...selected.values()];
    }

    /*
     * The parts of the message that are not whitelisted, as [start, end]
     * pairs. The rules only ever see these parts.
     */
    segments(message) {
        const self = this;

        let spans = [];

        for (const regex of self.whitelist) {
            for (const m of message.matchAll(regex)) {
                if (m[0].length > 0) {
                    spans.push([m.index, m.index + m[0].length]);
                }
            }
        }

        spans.sort((a, b) => (a[0] - b[0]) || (a[1] - b[1]));

        let segments = [];
        let position = 0;

        for (const [start, end] of spans) {
            if (start > position) {
                segments.push([position, start]);
            }

            position = Math.max(position, end);
        }

        if (position < message.length) {
            segments.push([position, message.length]);
        }

        return segments;
    }

    /*
     * Returns a list of {start, end, text, rules}, sorted by position.
     * Hits of different rules that overlap are merged into one match.
     */
    find_matches(message, rules = ALL) {
        const self = this;

        if (!message) {
            return [];
        }

        let found = [];

        const segments = self.segments(message);

        for (const rule of self.select(rules)) {
            for (const [seg_start, seg_end] of segments) {
                const segment = message.slice(seg_start, seg_end);

                for (const m of segment.matchAll(rule.regex)) {
                    if (m[0].length == 0) {
                        continue;
                    }

                    if (rule.exceptions.some(x => x.test(m[0]))) {
                        continue;
                    }

                    found.push([seg_start + m.index, seg_start + m.index + m[0].length, rule.name]);
                }
            }
        }

        found.sort((a, b) => (a[0] - b[0]) || (a[1] - b[1]) || (a[2] < b[2] ? -1 : a[2] > b[2] ? 1 : 0));

        let merged = [];

        for (const [start, end, name] of found) {
            const last = merged[merged.length - 1];

            if (last && start < last.end) {
                last.end = Math.max(last.end, end);

                if (!last.rules.includes(name)) {
                    last.rules.push(name);
                }

            } else {
                merged.push({ start: start, end: end, rules: [name] });
            }
        }

        merged.forEach(match => match.text = message.slice(match.start, match.end));

        return merged;
    }

    /*
     * Returns {text, hits, matches}.
     *
     * text is null when no rule hit. hits is the list of rule names that
     * hit, in rule order.
     *
     * With replace_value null each hit is wrapped in <mark> instead of
     * replaced.
     */
    filter_text(message, { rules = ALL, replace_value = '[*]' } = {}) {
        const self = this;

        const matches = self.find_matches(message, rules);

        if (matches.length == 0) {
            return { text: null, hits: [], matches: [] };
        }

        let pieces = [];
        let position = 0;
        let hit_names = new Set();

        for (const match of matches) {
            pieces.push(message.slice(position, match.start));

            pieces.push(replace_value !== null && replace_value !== undefined
                ? replace_value
                : `<mark>${match.text}</mark>`);

            position = match.end;
            match.rules.forEach(name => hit_names.add(name));
        }

        pieces.push(message.slice(position));

        const hits = self.select(rules)
            .map(rule => rule.name)
            .filter(name => hit_names.has(name));

        return { text: pieces.join(''), hits: hits, matches: matches };
    }
}

// So a caller can pass the plain rule set from the server again and again
// without compiling it each time.
const compiled = new WeakMap();

function as_ruleset(ruleset) {
    if (ruleset instanceof RuleSet) {
        return ruleset;
    }

    if (!compiled.has(ruleset)) {
        compiled.set(ruleset, new RuleSet(ruleset));
    }

    return compiled.get(ruleset);
}

export function find_matches(message, ruleset, rules = ALL) {
    return as_ruleset(ruleset).find_matches(message, rules);
}

export function filter_text(message, ruleset, options = {}) {
    return as_ruleset(ruleset).filter_text(message, options);
}

/*
 * Runs test cases against a rule set. A case has a name, a message, rules,
 * replace_value, expect_hits and, if the text matters too, expect_text.
 *
 * Returns one {name, text, hits, passed} per case.
 */
export function run_cases(ruleset, cases) {
    const compiled_ruleset = as_ruleset(ruleset);

    return cases.map(c => {
        const result = compiled_ruleset.filter_text(c.message,
            { rules: c.rules ?? ALL, replace_value: c.replace_value });

        const passed = JSON.stringify(result.hits) == JSON.stringify(c.expect_hits)
            && (!('expect_text' in c) || result.text === c.expect_text);

        return { name: c.name, text: result.text, hits: result.hits, passed: passed };
    });
}
